import React, { useState, useEffect } from 'react';
import { ArrowLeft, Mail, Lock, Eye, EyeOff, CheckCircle, AlertCircle, Shield, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { registrationService, type RegistrationData, type ValidationResult } from '@/features/auth/services/registrationService';
import { getRedirectConfig, formatTime, getCountdownColor } from '@/config/redirectConfig';
import { usePageFullScreenBackground } from '@/hooks/useFullScreenBackground';
import { notificationService } from '@/services/notificationService';
import { playClickSound } from '@/utils/soundUtils';

interface UserRegistrationProps {
    selectedProvider: {
        id: number | string;
        name: string;
        tagline: string;
        gradient: string;
        icon: string; // Path to icon image
    } | null;
    onBack: () => void;
    onRegistrationSuccess: (email: string) => void;
}

export default function UserRegistration({
    selectedProvider,
    onBack,
    onRegistrationSuccess
}: UserRegistrationProps) {
    // Full-screen background (uses centralized theme)
    const fullScreenBg = usePageFullScreenBackground();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});
    const [registrationSuccess, setRegistrationSuccess] = useState(false);
    const [registeredEmail, setRegisteredEmail] = useState('');
    const [countdownSeconds, setCountdownSeconds] = useState(0);
    const [emailActivationRequired, setEmailActivationRequired] = useState(false);
    const [redirectConfig] = useState(() => getRedirectConfig('registration'));
    
    // 2FA state
    const [enable2FA, setEnable2FA] = useState(false);

    // Countdown timer effect for registration redirect
    useEffect(() => {
        let interval: NodeJS.Timeout;

        if (registrationSuccess && redirectConfig.autoRedirectEnabled && countdownSeconds > 0) {
            interval = setInterval(() => {
                setCountdownSeconds(prev => {
                    if (prev <= 1) {
                        // Auto-redirect when countdown reaches 0
                        onRegistrationSuccess(registeredEmail);
                        return 0;
                    }
                    return prev - 1;
                });
            }, redirectConfig.countdownUpdateInterval);
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [registrationSuccess, countdownSeconds, redirectConfig, onRegistrationSuccess, registeredEmail]);


    const handleBackClick = () => {
        playClickSound();
        onBack();
    };


    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Clear previous validation errors
        setValidationErrors({});

        const registrationData: RegistrationData = {
            email: email.trim(),
            password: password,
            confirmPassword: confirmPassword,
            two_factor_enabled: enable2FA
            // Note: emailCode is not sent during registration
            // User will activate email first, then use OTP when logging in if 2FA is enabled
        };

        // Validate all fields
        const validation = registrationService.validateAllFields(registrationData);
        if (!validation.isValid) {
            setValidationErrors({ general: validation.message });
            return;
        }

        setIsLoading(true);
        playClickSound();

        try {
            const result = await registrationService.registerUser(registrationData);

            if (result.success) {
                // Always show registration success and start countdown
                setRegistrationSuccess(true);
                setRegisteredEmail(registrationData.email);

                // Check if email verification is required
                if (result.data && result.data.emailVerified === false) {
                    setEmailActivationRequired(true);
                } else {
                    setEmailActivationRequired(false);
                }

                // Always start countdown timer for auto-redirect to login
                if (redirectConfig.autoRedirectEnabled) {
                    setCountdownSeconds(redirectConfig.autoRedirectDelaySeconds);
                }

                notificationService.success(result.message);
            } else {
                setValidationErrors({ general: result.message });
                notificationService.error(result.message);
            }
        } catch (error: any) {
            const errorMessage = error.message || 'Registration failed. Please try again.';
            setValidationErrors({ general: errorMessage });
            notificationService.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const togglePasswordVisibility = () => {
        playClickSound();
        setShowPassword(!showPassword);
    };

    const toggleConfirmPasswordVisibility = () => {
        playClickSound();
        setShowConfirmPassword(!showConfirmPassword);
    };

    const handleManualRedirect = () => {
        playClickSound();
        onRegistrationSuccess(registeredEmail);
    };

    const validateField = (field: string, value: string) => {
        let validation: ValidationResult;

        switch (field) {
            case 'email':
                validation = registrationService.validateEmail(value);
                break;
            case 'password':
                validation = registrationService.validatePassword(value);
                break;
            case 'confirmPassword':
                validation = registrationService.validatePasswordConfirmation(password, value);
                break;
            default:
                return;
        }

        if (!validation.isValid) {
            setValidationErrors(prev => ({ ...prev, [field]: validation.message }));
        } else {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    // Show success screen
    if (registrationSuccess) {
        return (
            <div {...fullScreenBg} className={`${fullScreenBg.className} p-3 sm:p-4 lg:p-6`}>
                {/* Header with Back Button */}
                <div className="max-w-2xl mx-auto mb-3 sm:mb-4 lg:mb-6">
                    <button
                        onClick={handleBackClick}
                        className="flex items-center gap-2 text-white/80 hover:text-white transition-colors duration-200 mb-2 sm:mb-3"
                    >
                        <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span className="text-xs sm:text-sm font-medium">Back to Login</span>
                    </button>
                </div>

                {/* Success Content */}
                <div className="max-w-2xl mx-auto text-center">
                    <div className="mb-6 sm:mb-8">
                        <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-green-500/20 rounded-full mb-4 sm:mb-6">
                            <CheckCircle className="w-8 h-8 sm:w-10 sm:h-10 text-green-400" />
                        </div>
                        <h1 className="text-xl sm:text-2xl lg:text-3xl font-extrabold text-white mb-2 sm:mb-3">
                            {emailActivationRequired ? 'Registration Complete!' : 'Registration Successful!'}
                        </h1>
                        <p className="text-sm sm:text-base text-purple-200">
                            {emailActivationRequired
                                ? 'Please check your email to activate your account'
                                : 'Your account has been created successfully'
                            }
                        </p>
                    </div>

                    {/* Email Confirmation Notice - Only show when email activation is required */}
                    {emailActivationRequired && (
                        <div className="bg-white/5 backdrop-blur-md rounded-lg border border-white/10 p-4 sm:p-6 mb-6 sm:mb-8">
                            <div className="flex items-start gap-3 sm:gap-4">
                                <div className="flex-shrink-0">
                                    <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-blue-400 mt-0.5" />
                                </div>
                                <div className="text-left">
                                    <h3 className="text-white font-semibold text-sm sm:text-base mb-2">
                                        Check Your Email
                                    </h3>
                                    <p className="text-purple-200 text-xs sm:text-sm leading-relaxed mb-3">
                                        We've sent a confirmation link to <span className="text-white font-medium">{registeredEmail}</span>
                                    </p>
                                    <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                                        <div className="flex items-start gap-2">
                                            <AlertCircle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                                            <div>
                                                <p className="text-blue-200 text-xs font-medium mb-1">
                                                    Important:
                                                </p>
                                                <ul className="text-blue-200 text-xs space-y-1">
                                                    <li>• Click the confirmation link in your email</li>
                                                    <li>• Check your spam folder if you don't see it</li>
                                                    <li>• The link will expire in 24 hours</li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Auto-Redirect Notice with Countdown */}
                    <div className="text-center mb-8">
                        <div className="flex items-center justify-center gap-2 mb-4">
                            <Clock className="w-5 h-5 text-purple-300" />
                            <p className="text-purple-200 text-sm sm:text-base font-medium">
                                You will be navigated to the login page in
                            </p>
                        </div>
                        <div className={`text-2xl sm:text-3xl font-mono font-bold ${getCountdownColor(countdownSeconds, redirectConfig.autoRedirectDelaySeconds)} mb-4`}>
                            {countdownSeconds}s
                        </div>
                        <p className="text-purple-300 text-xs sm:text-sm mb-6">
                            Or click the button below to go immediately
                        </p>
                    </div>

                    {/* Manual Redirect Button */}
                    <div className="text-center">
                        <button
                            onClick={handleManualRedirect}
                            className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold text-sm sm:text-base rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all duration-200 hover:shadow-lg hover:scale-105 active:scale-95"
                        >
                            Go to Login Page Now
                        </button>
                    </div>
                </div>

                {/* Footer Info */}
                <div className="max-w-2xl mx-auto mt-4 sm:mt-6 lg:mt-8 text-center">
                    <div className="inline-flex items-center gap-2 sm:gap-3 px-4 sm:px-6 py-2 sm:py-3 bg-white/10 backdrop-blur-md rounded-full border border-white/20">
                        <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-green-400 animate-pulse" />
                        <span className="text-white font-medium text-xs sm:text-sm">Account created successfully</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div {...fullScreenBg} className={`${fullScreenBg.className} p-3 sm:p-4 lg:p-6`}>
            {/* Header with Back Button */}
            <div className="max-w-2xl mx-auto mb-3 sm:mb-4 lg:mb-6">
                <button
                    onClick={handleBackClick}
                    className="flex items-center gap-2 text-white/80 hover:text-white transition-colors duration-200 mb-2 sm:mb-3"
                >
                    <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span className="text-xs sm:text-sm font-medium">Back to Login</span>
                </button>

                {/* Selected Provider Info - Compact */}
                <div className="text-center mb-3 sm:mb-4 lg:mb-6">
                    <div className="inline-block mb-1 sm:mb-2 px-3 sm:px-4 py-1 sm:py-1.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full">
                        <span className="text-white font-bold text-xs tracking-wider">CREATE NEW ACCOUNT</span>
                    </div>
                    <h1 className="text-lg sm:text-xl lg:text-2xl font-extrabold text-white mb-1 sm:mb-2 drop-shadow-2xl">
                        {selectedProvider?.name || 'Kiosk Gaming'}
                    </h1>
                    <p className="text-xs sm:text-sm text-purple-200">
                        Create your account to start playing
                    </p>
                </div>
            </div>

            {/* Registration Form */}
            <div className="max-w-2xl mx-auto">
                <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                    {/* Email Field */}
                    <div className="relative">
                        <label htmlFor="email" className="block text-sm font-medium text-white mb-1">
                            Email Address
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Mail className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                            </div>
                            <input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    validateField('email', e.target.value);
                                }}
                                onBlur={(e) => validateField('email', e.target.value)}
                                autoComplete="email"
                                className={`block w-full pl-10 pr-3 py-2 sm:py-3 border rounded-lg bg-white/10 backdrop-blur-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent text-sm sm:text-base ${validationErrors.email ? 'border-red-400 focus:ring-red-500' : 'border-gray-300 focus:ring-purple-500'
                                    }`}
                                placeholder="Enter your email address"
                                required
                            />
                        </div>
                        {validationErrors.email && (
                            <p className="mt-1 text-xs text-red-400">{validationErrors.email}</p>
                        )}
                    </div>

                    {/* Password Field */}
                    <div className="relative">
                        <label htmlFor="password" className="block text-sm font-medium text-white mb-1">
                            Password
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Lock className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                            </div>
                            <input
                                id="password"
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => {
                                    setPassword(e.target.value);
                                    validateField('password', e.target.value);
                                }}
                                onBlur={(e) => validateField('password', e.target.value)}
                                autoComplete="new-password"
                                className={`block w-full pl-10 pr-12 py-2 sm:py-3 border rounded-lg bg-white/10 backdrop-blur-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent text-sm sm:text-base ${validationErrors.password ? 'border-red-400 focus:ring-red-500' : 'border-gray-300 focus:ring-purple-500'
                                    }`}
                                placeholder="Create a strong password"
                                required
                            />
                            <button
                                type="button"
                                onClick={togglePasswordVisibility}
                                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white transition-colors duration-200"
                            >
                                {showPassword ? (
                                    <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" />
                                ) : (
                                    <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
                                )}
                            </button>
                        </div>
                        {validationErrors.password && (
                            <p className="mt-1 text-xs text-red-400">{validationErrors.password}</p>
                        )}
                    </div>

                    {/* Confirm Password Field */}
                    <div className="relative">
                        <label htmlFor="confirmPassword" className="block text-sm font-medium text-white mb-1">
                            Confirm Password
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Lock className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                            </div>
                            <input
                                id="confirmPassword"
                                type={showConfirmPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => {
                                    setConfirmPassword(e.target.value);
                                    validateField('confirmPassword', e.target.value);
                                }}
                                onBlur={(e) => validateField('confirmPassword', e.target.value)}
                                autoComplete="new-password"
                                className={`block w-full pl-10 pr-12 py-2 sm:py-3 border rounded-lg bg-white/10 backdrop-blur-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent text-sm sm:text-base ${validationErrors.confirmPassword ? 'border-red-400 focus:ring-red-500' : 'border-gray-300 focus:ring-purple-500'
                                    }`}
                                placeholder="Confirm your password"
                                required
                            />
                            <button
                                type="button"
                                onClick={toggleConfirmPasswordVisibility}
                                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white transition-colors duration-200"
                            >
                                {showConfirmPassword ? (
                                    <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" />
                                ) : (
                                    <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
                                )}
                            </button>
                        </div>
                        {validationErrors.confirmPassword && (
                            <p className="mt-1 text-xs text-red-400">{validationErrors.confirmPassword}</p>
                        )}
                    </div>

                    {/* 2FA Toggle */}
                    <div className="relative">
                        <div className="flex items-center gap-3 p-3 sm:p-4 bg-white/5 backdrop-blur-md rounded-lg border border-white/10">
                            <input
                                type="checkbox"
                                id="enable2FA"
                                checked={enable2FA}
                                onChange={(e) => {
                                    playClickSound();
                                    setEnable2FA(e.target.checked);
                                }}
                                className="w-5 h-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500 focus:ring-2"
                            />
                            <label htmlFor="enable2FA" className="flex items-center gap-2 cursor-pointer flex-1">
                                <Shield className={`w-5 h-5 ${enable2FA ? 'text-green-400' : 'text-gray-400'}`} />
                                <div>
                                    <span className="text-white font-medium text-sm">Enable Two-Factor Authentication (2FA)</span>
                                    <p className="text-purple-200 text-xs mt-0.5">Additional security for your account</p>
                                </div>
                            </label>
                        </div>
                    </div>

                    {/* 2FA Info - Show when 2FA is enabled */}
                    {enable2FA && (
                        <div className="p-3 sm:p-4 bg-blue-500/10 backdrop-blur-md rounded-lg border border-blue-400/30">
                            <div className="flex items-start gap-2">
                                <Shield className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                                <div className="text-xs text-blue-200">
                                    <p className="font-medium mb-1">2FA will be activated after email verification</p>
                                    <p>After you activate your email, you'll need additional verification when logging in. If you don't enable 2FA now, you can enable it later in Settings.</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* General Error Message */}
                    {validationErrors.general && (
                        <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg">
                            <p className="text-red-400 text-sm">{validationErrors.general}</p>
                        </div>
                    )}

                    {/* Submit Button */}
                    <button
                        type="submit"
                        disabled={isLoading || !email.trim() || !password.trim() || !confirmPassword.trim()}
                        className={`
                            w-full py-3 sm:py-4 px-4 rounded-lg font-bold text-sm sm:text-base transition-all duration-300
                            ${isLoading || !email.trim() || !password.trim() || !confirmPassword.trim()
                                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                                : 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:shadow-lg hover:scale-105 active:scale-95'
                            }
                        `}
                    >
                        {isLoading ? (
                            <div className="flex items-center justify-center gap-2">
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                <span>Creating Account...</span>
                            </div>
                        ) : (
                            'Create Account'
                        )}
                    </button>
                </form>

                {/* Password Requirements */}
                <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-white/5 backdrop-blur-md rounded-lg border border-white/10">
                    <h4 className="text-white font-semibold text-xs sm:text-sm mb-2">
                        Password Requirements:
                    </h4>
                    <ul className="text-purple-200 text-xs space-y-1">
                        <li>• At least 8 characters long</li>
                        <li>• One uppercase letter (A-Z)</li>
                        <li>• One lowercase letter (a-z)</li>
                        <li>• One number (0-9)</li>
                        <li>• One special character (!@#$%^&*)</li>
                    </ul>
                </div>
            </div>

            {/* Already have an account link */}
            <div className="max-w-2xl mx-auto mt-4 sm:mt-6 text-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-md rounded-lg border border-white/10 hover:bg-white/10 transition-all duration-200">
                    <span className="text-purple-200 text-sm">Already have an account?</span>
                    <button
                        onClick={handleBackClick}
                        className="text-white font-semibold text-sm hover:text-purple-200 transition-colors duration-200 underline decoration-purple-400 hover:decoration-purple-200"
                    >
                        Sign in here
                    </button>
                </div>
            </div>

            {/* Footer Info */}
            <div className="max-w-2xl mx-auto mt-4 sm:mt-6 lg:mt-8 text-center">
                <div className="inline-flex items-center gap-2 sm:gap-3 px-4 sm:px-6 py-2 sm:py-3 bg-white/10 backdrop-blur-md rounded-full border border-white/20">
                    <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-green-400 animate-pulse" />
                    <span className="text-white font-medium text-xs sm:text-sm">Secure registration process</span>
                </div>
            </div>
        </div>
    );
}
